import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const ImportFlagRulesModal = ({ open, onClose, onImport, currentProjectId }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (open) {
      fetchProjects();
      // Reset states when modal opens
      setSelectedProjects([]);
      setSearchQuery('');
      setError('');
      setSuccessMessage('');
    }
  }, [open]);

  useEffect(() => {
    // Filter projects based on search query
    if (searchQuery.trim() === '') {
      setFilteredProjects(projects);
    } else {
      const filtered = projects.filter((project) =>
        project.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProjects(filtered);
    }
  }, [searchQuery, projects]);

  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const url =
        'https://g23i2ede0i.execute-api.ap-south-1.amazonaws.com/dev/athrv/api/v1/platform/sonarqube/import-flag-projects';

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Projects response:', data);

      // Filter out current project from the list
      const projectsList = (data.projects || []).filter(
        (project) => project.project_id !== currentProjectId
      );
      setProjects(projectsList);
      setFilteredProjects(projectsList);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProject = (projectId) => {
    setSelectedProjects((prev) => {
      if (prev.includes(projectId)) {
        return prev.filter((id) => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === filteredProjects.length) {
      // Deselect all
      setSelectedProjects([]);
    } else {
      // Select all filtered projects
      setSelectedProjects(filteredProjects.map((p) => p.project_id));
    }
  };

  const handleImport = async () => {
    if (selectedProjects.length === 0) {
      setError('Please select at least one project');
      return;
    }

    setImporting(true);
    setError('');
    setSuccessMessage('');

    try {
      const url = 'https://g23i2ede0i.execute-api.ap-south-1.amazonaws.com/dev/athrv/api/v1/platform/sonarqube/get-flag-issues-multiple';
      
      console.log('Importing flag rules TO project:', currentProjectId);
      console.log('Importing flag rules FROM projects:', selectedProjects);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          target_project_id: currentProjectId,
          project_ids: selectedProjects,
        }),
      });

      if (!response.ok) {
        throw new Error(`Import failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Import response:', data);

      // Show success message
      const totalIssues = data.total_issues || 0;
      const totalProjects = data.total_projects || 0;
      setSuccessMessage(
        `Successfully imported ${totalIssues} flagged issue${totalIssues !== 1 ? 's' : ''} from ${totalProjects} project${totalProjects !== 1 ? 's' : ''}`
      );

      // Call the parent callback with the imported data
      onImport(data);

      // Close modal after a short delay to show success message
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error importing flag rules:', err);
      setError('Failed to import flag rules. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    if (!importing) {
      setSelectedProjects([]);
      setSearchQuery('');
      setError('');
      setSuccessMessage('');
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableEscapeKeyDown={importing}
      PaperProps={{
        sx: {
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Import Flag Rules from Projects</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select projects to import their flagged rules into the current project
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '300px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 2 }}
              disabled={importing}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />

            {/* Select All Checkbox */}
            <Box sx={{ mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      filteredProjects.length > 0 &&
                      selectedProjects.length === filteredProjects.length
                    }
                    indeterminate={
                      selectedProjects.length > 0 &&
                      selectedProjects.length < filteredProjects.length
                    }
                    onChange={handleSelectAll}
                    disabled={importing}
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="bold">
                    Select All ({selectedProjects.length} of {filteredProjects.length} selected)
                  </Typography>
                }
              />
            </Box>

            {/* Projects List */}
            <List
              sx={{
                maxHeight: '400px',
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {filteredProjects.length > 0 ? (
                filteredProjects.map((project) => (
                  <ListItem key={project.project_id} disablePadding>
                    <ListItemButton
                      onClick={() => handleToggleProject(project.project_id)}
                      dense
                      disabled={importing}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedProjects.includes(project.project_id)}
                          tabIndex={-1}
                          disableRipple
                          disabled={importing}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={project.name}
                        secondary={`ID: ${project.project_id}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText
                    primary="No projects found"
                    sx={{ textAlign: 'center', py: 4 }}
                  />
                </ListItem>
              )}
            </List>

            {/* Selected Count */}
            {selectedProjects.length > 0 && !successMessage && (
              <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
                {selectedProjects.length} project(s) selected
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={importing}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={loading || importing || selectedProjects.length === 0}
          sx={{
            backgroundColor: 'rgb(12, 20, 50)',
            '&:hover': {
              backgroundColor: 'rgba(55, 55, 125, 0.9)',
            },
          }}
        >
          {importing ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Importing...
            </>
          ) : (
            'Import Selected Rules'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportFlagRulesModal;